import { getCanonicalRobotName } from '../utils/robotNaming';
import { normalizeCanonicalEvent } from './canonicalEvent';

export function toDate(value) {
  if (value === null || value === undefined || value === '') return null;
  try {
    const seconds = value?.seconds ?? value?._seconds;
    const parsed = value instanceof Date ? value
      : typeof value?.toDate === 'function' ? value.toDate()
      : typeof seconds === 'number' ? new Date(seconds * 1000 + Number(value.nanoseconds ?? value._nanoseconds ?? 0) / 1e6)
      : new Date(value);
    return parsed instanceof Date && Number.isFinite(parsed.getTime()) ? parsed : null;
  } catch { return null; }
}

export function sortRecordsNewestFirst(rows = [], getDate = row => toDate(row.timestamp) || toDate(row.date) || toDate(row.createdAt)) {
  return [...rows].sort((a, b) => {
    const difference = (toDate(getDate(b))?.getTime() ?? -Infinity) - (toDate(getDate(a))?.getTime() ?? -Infinity);
    return (Number.isNaN(difference) ? 0 : difference) || String(a.id ?? '').localeCompare(String(b.id ?? ''));
  });
}

export function csvEscape(value) {
  const text = String(value ?? '');
  const safe = typeof value === 'string' && /^[=+@\-\t\r]/.test(text) ? "'" + text : text;
  return '"' + safe.replace(/"/g, '""') + '"';
}

export function normalizeText(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function isYesLike(value) {
  const normalized = normalizeText(value);
  return ['ja', 'yes', 'true', '1', 'y'].includes(normalized);
}

function normalizeIncidentFlag(value) {
  const normalized = normalizeText(value);
  if (!normalized) return '';
  if (normalized.includes('klep open') || normalized.includes('klepopen')) return 'klep open';
  if (normalized.includes('kop geblokkeerd') || normalized.includes('kop blokkade')) return 'kop geblokkeerd';
  if (normalized.includes('vast in doel') || normalized === 'in doel' || normalized.includes(' in doel')) return 'in doel';
  if (normalized.includes('doosje mesjes afgeleverd')) return 'doosje mesjes afgeleverd';
  return normalized;
}

function incidentFlagLabel(value) {
  const normalized = normalizeIncidentFlag(value);
  if (!normalized) return '';
  if (normalized === 'klep open') return 'Klep open';
  if (normalized === 'kop geblokkeerd') return 'Kop geblokkeerd';
  if (normalized === 'in doel') return 'In doel';
  if (normalized === 'doosje mesjes afgeleverd') return 'Doosje mesjes afgeleverd';
  return normalized;
}

export function collectIncidentFlags(record = {}) {
  const flags = new Set();
  const addFlag = (value) => {
    const normalized = normalizeIncidentFlag(value);
    if (normalized) flags.add(normalized);
  };

  (Array.isArray(record.meldingIncidentFlags) ? record.meldingIncidentFlags : []).forEach(addFlag);

  if (isYesLike(record.klepOpen) || isYesLike(record.klep) || isYesLike(record.klep_open) || isYesLike(record.klepopen)) {
    addFlag('klep open');
  }
  if (isYesLike(record.kopGeblokkeerd) || isYesLike(record.kop_geblokkeerd)) {
    addFlag('kop geblokkeerd');
  }
  if (isYesLike(record.vastInDoel) || isYesLike(record.inDoel) || isYesLike(record.indoel) || isYesLike(record.inTarget)) {
    addFlag('in doel');
  }

  const textHaystack = normalizeText([
    record.bijzonderheden,
    record.details,
    record.opmerkingen,
    record.onderdelen,
  ].filter(Boolean).join(' '));

  if (textHaystack.includes('klep open') || textHaystack.includes('klepopen')) addFlag('klep open');
  if (textHaystack.includes('kop geblokkeerd')) addFlag('kop geblokkeerd');
  if (textHaystack.includes('vast in doel') || textHaystack.includes(' in doel')) addFlag('in doel');
  if (textHaystack.includes('doosje mesjes afgeleverd')) addFlag('doosje mesjes afgeleverd');

  return Array.from(flags);
}

export function resolveMeldingType(record = {}) {
  const explicitType = normalizeText(record.meldingType || record.type || '');
  if (['automatisch', 'auto', 'email_intake'].includes(explicitType)) return 'automatisch';
  if (['handmatig', 'manual'].includes(explicitType)) return 'handmatig';

  const normalizedSource = normalizeText(record.source || record.sourceType || '');
  const isAuto = Boolean(
    record.isAutoMelding
    || normalizedSource === 'email_intake'
    || normalizedSource === 'vestiging_auto'
    || normalizedSource === 'myrobot_email'
  );

  return isAuto ? 'automatisch' : 'handmatig';
}

export function isAutomaticMelding(record = {}) {
  return resolveMeldingType(record) === 'automatisch';
}

function meldingTypeLabel(value) {
  const normalized = normalizeText(value);
  if (normalized === 'automatisch') return 'Automatisch';
  if (normalized === 'handmatig') return 'Handmatig';
  return '';
}

function normalizeRobotComparable(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveRobotIdentityKey({ robotDocumentId = '', kNummer = '', projectNummer = '', gemeente = '', robotCanonical = '' } = {}) {
  if (robotDocumentId) return `robot:${robotDocumentId}`;
  const normalizedK = normalizeText(kNummer);
  if (normalizedK) return `k:${normalizedK}`;

  const normalizedProject = normalizeText(projectNummer);
  const normalizedGemeente = normalizeText(gemeente);
  if (normalizedProject && normalizedGemeente) return `pg:${normalizedProject}|${normalizedGemeente}`;

  return `name:${normalizeRobotComparable(robotCanonical)}`;
}

export function normalizeBooleanLabel(value) {
  const normalized = normalizeText(value);
  if (!normalized) return '';
  if (['ja', 'yes', 'true', '1', 'in doel', 'indoel', 'y'].includes(normalized)) return 'ja';
  if (['nee', 'no', 'false', '0', 'n'].includes(normalized)) return 'nee';
  return normalized;
}

export function asNumber(value) {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const normalized = String(value).replace(',', '.');
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function fmtHours(value) {
  return Number(value || 0).toFixed(2);
}

export function resolveRecordTimestamp(record) {
  return (
    toDate(record?.timestamp)
    || toDate(record?.createdAt)
    || toDate(record?.meldingDateTime)
    || toDate(record?.datum)
    || toDate(record?.meldingDatum)
    || toDate(record?.date)
    || null
  );
}

function resolveRobotMeta(record) {
  const selectedRobot = record?.selectedRobot || {};
  const nestedRobot = selectedRobot?.robot || {};

  return {
    gemeente: String(
      record?.gemeente
      || record?.city
      || record?.robotGemeente
      || selectedRobot?.gemeente
      || nestedRobot?.gemeente
      || ''
    ).trim(),
    projectNummer: String(
      record?.projectNummer
      || record?.project
      || record?.projectCode
      || record?.robotProject
      || selectedRobot?.projectNummer
      || selectedRobot?.projectCode
      || nestedRobot?.projectNummer
      || nestedRobot?.projectCode
      || ''
    ).trim(),
    kNummer: String(
      record?.kNummer
      || record?.k
      || record?.kNumber
      || record?.robotKNummer
      || selectedRobot?.kNummer
      || selectedRobot?.kNumber
      || nestedRobot?.kNummer
      || nestedRobot?.kNumber
      || ''
    ).trim(),
  };
}

export function resolveDisplayName(record) {
  const rawName = [
    record?.displayName,
    record?.gebruikerNaam,
    record?.technician,
    record?.username,
    record?.user,
    record?.createdBy,
    record?.updatedBy,
  ].find((value) => String(value || '').trim());

  if (rawName) return String(rawName).trim();

  const email = String(record?.email || '').trim();
  if (email.includes('@')) {
    const local = email.split('@')[0] || '';
    const firstToken = local.split(/[._-]+/)[0] || local;
    if (!firstToken) return '';
    return firstToken.charAt(0).toUpperCase() + firstToken.slice(1);
  }
  return email;
}

export function buildAnalyticsRecords(meldingen = []) {
  return (meldingen || []).map((melding) => {
    const timestamp = resolveRecordTimestamp(melding);
    const userName = resolveDisplayName(melding);
    const workHours = asNumber(melding.uren ?? melding.hours ?? 0);
    const heenReis = asNumber(melding.heenReistijdHours ?? melding.heenReisTijd ?? 0);
    const terugReis = asNumber(melding.terugReistijdHours ?? melding.terugReisTijd ?? 0);
    const explicitTravel = asNumber(melding.reistijdHours ?? melding.reisTijd ?? melding.totalTravelHours ?? 0);
    const travelHours = explicitTravel > 0 ? explicitTravel : heenReis + terugReis;
    const totalHours = workHours + travelHours;
    const travelDeviation = travelHours - workHours;
    const { kNummer: rawKNummer, projectNummer: rawProjectNummer, gemeente: rawGemeente } = resolveRobotMeta(melding);
    const selectedRobot = melding?.selectedRobot || {};
    const nestedRobot = selectedRobot?.robot || {};
    const robotModel = String(
      melding?.robotModel
      || melding?.model
      || melding?.robotType
      || melding?.type
      || selectedRobot?.model
      || selectedRobot?.type
      || nestedRobot?.model
      || nestedRobot?.type
      || ''
    ).trim();

    const robotCanonical = getCanonicalRobotName({
      ...melding,
      kNummer: rawKNummer,
      kNumber: rawKNummer,
      projectNummer: rawProjectNummer,
      projectCode: rawProjectNummer,
      gemeente: rawGemeente,
    }, 'Onbekend', { preferLookup: true });

    const robotCanonicalLookupOnly = getCanonicalRobotName({
      ...melding,
      kNummer: rawKNummer,
      projectNummer: rawProjectNummer,
      gemeente: rawGemeente,
    }, '', { preferLookup: true });

    const robotIdentityKey = resolveRobotIdentityKey({
      robotDocumentId: melding.robotDocumentId,
      kNummer: rawKNummer,
      projectNummer: rawProjectNummer,
      gemeente: rawGemeente,
      robotCanonical: robotCanonicalLookupOnly || robotCanonical,
    });
    const meldingTypeNormalized = resolveMeldingType(melding);
    const canonicalEvent = melding.canonicalEvent || normalizeCanonicalEvent(melding);

    return {
      id: melding.id || melding._id || '',
      timestamp,
      year: timestamp ? timestamp.getFullYear() : '',
      month: timestamp ? timestamp.getMonth() + 1 : '',
      robotCanonical,
      robotCanonicalLookupOnly,
      robotDisplayLabel: robotCanonicalLookupOnly || robotCanonical,
      robotCanonicalNormalized: normalizeText(robotCanonical),
      robotCanonicalComparable: normalizeRobotComparable(robotCanonical),
      robotCanonicalLookupComparable: normalizeRobotComparable(robotCanonicalLookupOnly),
      robotIdentityKey,
      robotModel,
      robotDocumentId: melding.robotDocumentId || '',
      gemeente: rawGemeente,
      gemeenteNormalized: normalizeText(melding?.gemeente || melding?.city || ''),
      projectNummer: rawProjectNummer,
      projectNormalized: normalizeText(melding?.projectNummer || melding?.project || ''),
      kNummer: rawKNummer,
      meldingTypeNormalized,
      meldingTypeLabel: meldingTypeLabel(meldingTypeNormalized),
      canonicalEvent,
      isHuman: canonicalEvent.isHuman,
      isAutomatic: canonicalEvent.isAutomatic,
      isVisit: canonicalEvent.isVisit,
      isMaintenance: canonicalEvent.isMaintenance,
      isBladeRelated: canonicalEvent.isBladeRelated,
      displayName: userName,
      displayNameNormalized: normalizeText(userName),
      workHours,
      heenReis,
      terugReis,
      travelHours,
      totalHours,
      travelDeviation,
      mesjes: asNumber(melding?.mesjes ?? 0),
      isNormaleMelding: (() => {
        const raw = melding?.isNormaleMelding ?? melding?.meldingNormaal;
        if (typeof raw === 'boolean') return raw;
        return String(raw ?? 'Ja').trim().toLowerCase() !== 'nee';
      })(),
      inDoelNormalized: normalizeBooleanLabel(melding?.inDoel ?? melding?.indoel ?? melding?.inTarget ?? ''),
      bijzonderheden: String(melding?.bijzonderheden || melding?.details || melding?.opmerkingen || '').trim(),
      onderdelen: String(melding?.onderdelen || '').trim(),
      incidentFlags: Array.isArray(melding?.meldingIncidentFlags) ? melding.meldingIncidentFlags : [],
      resolvedIncidentFlags: collectIncidentFlags(melding),
      incidentFlagsNormalized: Array.isArray(melding?.meldingIncidentFlags)
        ? melding.meldingIncidentFlags.map(flag => normalizeText(flag))
        : [],
    };
  });
}

export function recordMatchesAnalyticsFilters(record, filters = {}) {
  const {
    quickSearch = '',
    dateFrom = '',
    dateTo = '',
    robotFilter = '',
    gemeenteFilter = '',
    userFilter = '',
    yearFilter = '',
    bijzonderhedenFilter = '',
    meldingTypeFilter = '',
  } = filters;

  const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
  const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
  const normalizedQuickSearch = normalizeText(quickSearch);

  if (from && (!record.timestamp || record.timestamp < from)) return false;
  if (to && (!record.timestamp || record.timestamp > to)) return false;
  if (robotFilter) {
    const normalizedRobotFilter = normalizeText(robotFilter);
    const comparableRobotFilter = normalizeRobotComparable(robotFilter);
    const matchesByKey = String(robotFilter).startsWith('k:')
      || String(robotFilter).startsWith('pg:')
      || String(robotFilter).startsWith('name:');

    if (matchesByKey) {
      if (record.robotIdentityKey !== String(robotFilter)) return false;
    } else {
      const equalsCanonical = record.robotCanonicalNormalized === normalizedRobotFilter;
      const equalsLookup = normalizeText(record.robotCanonicalLookupOnly) === normalizedRobotFilter;
      const comparableMatch = record.robotCanonicalComparable === comparableRobotFilter
        || record.robotCanonicalLookupComparable === comparableRobotFilter;
      if (!equalsCanonical && !equalsLookup && !comparableMatch) return false;
    }
  }
  if (gemeenteFilter && record.gemeenteNormalized !== normalizeText(gemeenteFilter)) return false;
  if (userFilter && record.displayNameNormalized !== normalizeText(userFilter)) return false;
  if (yearFilter && String(record.year || '') !== String(yearFilter)) return false;
  if (meldingTypeFilter && record.meldingTypeNormalized !== normalizeText(meldingTypeFilter)) return false;
  if (bijzonderhedenFilter) {
    const normalizedBijzonderFilter = normalizeText(bijzonderhedenFilter);
    const hasIncidentFlag = (Array.isArray(record.resolvedIncidentFlags) ? record.resolvedIncidentFlags : [])
      .map((flag) => normalizeIncidentFlag(flag))
      .includes(normalizedBijzonderFilter);
    const textHaystack = normalizeText([record.bijzonderheden, record.onderdelen].filter(Boolean).join(' '));
    const hasTextMatch = textHaystack.includes(normalizedBijzonderFilter);
    if (!hasIncidentFlag && !hasTextMatch) return false;
  }

  if (normalizedQuickSearch) {
    const haystack = normalizeText([
      record.robotCanonical,
      record.gemeente,
      record.projectNummer,
      record.kNummer,
      record.displayName,
      record.meldingTypeLabel,
      record.bijzonderheden,
      record.onderdelen,
      Array.isArray(record.resolvedIncidentFlags) ? record.resolvedIncidentFlags.join(' ') : '',
    ].filter(Boolean).join(' '));
    if (!haystack.includes(normalizedQuickSearch)) return false;
  }

  return true;
}

export function buildAnalyticsOptions(records = []) {
  const addSorted = (selector) => Array.from(new Set(records.map(selector).filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b), 'nl', { sensitivity: 'base' }));

  const bijzonderheiden = Array.from(new Set(
    records
      .flatMap(record => Array.isArray(record.resolvedIncidentFlags) ? record.resolvedIncidentFlags : [])
      .map(flag => incidentFlagLabel(flag))
      .filter(Boolean)
  )).sort((a, b) => String(a).localeCompare(String(b), 'nl', { sensitivity: 'base' }));

  const robotsByDisplayName = new Map();
  records.forEach((record) => {
    const label = String(record?.robotDisplayLabel || record?.robotCanonical || '').trim();
    if (!label) return;

    const displayKey = normalizeRobotComparable(label);
    if (!displayKey) return;

    const existing = robotsByDisplayName.get(displayKey);
    if (!existing) {
      robotsByDisplayName.set(displayKey, {
        // Filter on comparable canonical name so visually equal robots never duplicate in dropdowns.
        value: displayKey,
        label,
        count: 1,
      });
      return;
    }

    existing.count += 1;
    const preferred = String(record.robotDisplayLabel || record.robotCanonical || '').trim();
    if (preferred && preferred.length > String(existing.label || '').length) {
      existing.label = preferred;
    }
  });

  const robots = Array.from(robotsByDisplayName.values())
    .sort((a, b) => String(a.label).localeCompare(String(b.label), 'nl', { sensitivity: 'base' }))
    .map(({ value, label }) => ({ value, label }));

  return {
    robots,
    gemeentes: addSorted((record) => record.gemeente),
    users: addSorted((record) => record.displayName),
    years: addSorted((record) => record.year),
    meldingTypes: addSorted((record) => record.meldingTypeLabel),
    bijzonderheiden,
  };
}
